/**
 * ActionsSection - grouped list of `WorkflowAction` rows for the
 * workflow's current version. Groups actions by applicability
 * (Creation / State / Global) so admins can reason about what fires
 * and when without scanning a flat list.
 *
 * Rows open the large <ActionModal /> for edit. Delete is guarded by
 * a confirm prompt.
 */

import { useMemo, useState } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { ConfirmationDialog } from '@/admin/components/shared/confirmation-dialog'
import {
  useDeleteAction,
  type WorkflowActionAdmin,
  type WorkflowDefinitionAdmin,
  type WorkflowStateAdmin,
  type WorkflowVersionAdmin,
} from '@/admin/hooks/use-workflow-definitions'
import { toastApiError } from '@/shared/lib/api-error'

import { ActionModal } from './action-modal'

interface ActionsSectionProps {
  workflow: WorkflowDefinitionAdmin
  version: WorkflowVersionAdmin
}

export function ActionsSection({ workflow, version }: ActionsSectionProps) {
  const [editing, setEditing] = useState<WorkflowActionAdmin | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [deleting, setDeleting] = useState<WorkflowActionAdmin | null>(null)
  const remove = useDeleteAction(workflow.uuid)

  const groups = useMemo(() => buildGroups(version), [version])

  async function handleDelete() {
    if (!deleting) return
    try {
      await remove.mutateAsync(deleting.uuid)
      toast.success('Action deleted')
      setDeleting(null)
    } catch (err) {
      toastApiError(err, 'Failed to delete action')
    }
  }

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h3 className="text-[12px] font-medium uppercase tracking-[0.12em] text-[var(--ink-2)]">
          Actions
        </h3>
        <Button
          variant="outline"
          size="xs"
          onClick={() => setCreateOpen(true)}
          data-testid="workflows-actions-add"
        >
          <Plus size={11} className="mr-1" />
          Add action
        </Button>
      </div>

      {groups.length === 0 ? (
        <div className="rounded-[6px] border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-4 text-center text-[11px] text-[var(--ink-3)]">
          No actions yet. Add one to let users transition instances.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {groups.map((group) => (
            <ActionGroup
              key={group.key}
              title={group.title}
              subtitle={group.subtitle}
              color={group.color}
              actions={group.actions}
              onEdit={setEditing}
              onDelete={setDeleting}
            />
          ))}
        </div>
      )}

      <ActionModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode="create"
        workflow={workflow}
        version={version}
      />
      <ActionModal
        open={!!editing}
        onOpenChange={(next) => {
          if (!next) setEditing(null)
        }}
        mode="edit"
        workflow={workflow}
        version={version}
        action={editing ?? undefined}
      />
      <ConfirmationDialog
        open={!!deleting}
        onOpenChange={(next) => {
          if (!next) setDeleting(null)
        }}
        title={`Delete ${deleting?.label ?? 'action'}?`}
        description="This removes the action from the workflow version. Existing instances will no longer be able to use it."
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        destructive
        isPending={remove.isPending}
        onConfirm={handleDelete}
      />
    </section>
  )
}

interface GroupData {
  key: string
  title: string
  subtitle: string
  color?: string
  actions: WorkflowActionAdmin[]
}

function buildGroups(version: WorkflowVersionAdmin): GroupData[] {
  const states = [...version.states].sort((a, b) => a.order - b.order)
  const stateByKey = new Map<string, WorkflowStateAdmin>(
    states.map((s) => [s.key, s]),
  )

  const groups: GroupData[] = []
  const creation = version.actions.filter((a) => a.applicability === 'CREATION')
  const global = version.actions.filter((a) => a.applicability === 'GLOBAL')

  if (creation.length > 0) {
    groups.push({
      key: '__creation__',
      title: 'Creation actions',
      subtitle:
        'Evaluated when a new resource is created. The highest-priority matching action runs.',
      actions: creation.sort(sort),
    })
  }

  for (const state of states) {
    const outgoing = version.actions.filter(
      (a) => a.applicability === 'STATE' && a.source_state_key === state.key,
    )
    if (outgoing.length === 0) continue
    groups.push({
      key: state.key,
      title: state.label,
      subtitle: `Available when the resource is in ${state.label}.`,
      color: state.color,
      actions: outgoing.sort(sort),
    })
  }

  if (global.length > 0) {
    groups.push({
      key: '__global__',
      title: 'Global actions',
      subtitle: 'Available regardless of the current state.',
      actions: global.sort(sort),
    })
  }

  const placed = new Set(groups.flatMap((g) => g.actions.map((a) => a.uuid)))
  const orphans = version.actions.filter((a) => !placed.has(a.uuid))
  if (orphans.length > 0) {
    groups.push({
      key: '__orphans__',
      title: 'Unattached actions',
      subtitle:
        'Actions whose source state is missing from this version. Review and reattach.',
      actions: orphans.sort(sort),
    })
  }

  void stateByKey
  return groups
}

function sort(a: WorkflowActionAdmin, b: WorkflowActionAdmin): number {
  if (b.priority !== a.priority) return b.priority - a.priority
  return a.order - b.order
}

interface ActionGroupProps {
  title: string
  subtitle: string
  color?: string
  actions: WorkflowActionAdmin[]
  onEdit: (action: WorkflowActionAdmin) => void
  onDelete: (action: WorkflowActionAdmin) => void
}

function ActionGroup({
  title,
  subtitle,
  color,
  actions,
  onEdit,
  onDelete,
}: ActionGroupProps) {
  return (
    <div className="overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)]">
      <div className="flex items-start gap-2 border-b-[0.5px] border-b-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-2">
        {color && (
          <span
            className="mt-1 inline-block h-2 w-2 shrink-0 rounded-full border-[0.5px] border-[var(--rule-strong)]"
            style={{ background: color }}
          />
        )}
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-2)]">
            {title}
          </p>
          <p className="text-[10px] text-[var(--ink-3)]">{subtitle}</p>
        </div>
      </div>
      {actions.map((action) => (
        <ActionRow
          key={action.uuid}
          action={action}
          onEdit={() => onEdit(action)}
          onDelete={() => onDelete(action)}
        />
      ))}
    </div>
  )
}

function ActionRow({
  action,
  onEdit,
  onDelete,
}: {
  action: WorkflowActionAdmin
  onEdit: () => void
  onDelete: () => void
}) {
  return (
    <div
      className="flex items-center gap-2 border-t-[0.5px] border-t-[var(--rule)] bg-[var(--bg)] px-3 py-2 first:border-t-0"
      data-testid={`workflows-action-row-${action.uuid}`}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <span className="truncate text-[12px] text-[var(--ink)]">
          {action.label}
        </span>
        <Badge variant="outline" className="font-mono text-[10px]">
          {action.key}
        </Badge>
        <Badge variant="secondary" className="text-[10px] uppercase">
          {action.actor}
        </Badge>
        {action.target_state_key && (
          <span className="text-[10px] text-[var(--ink-3)]">
            → {action.target_state_label ?? action.target_state_key}
          </span>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <CountBadge
          count={action.side_effects.length}
          label="fx"
          title="Side effects"
        />
        <Badge variant="outline" className="text-[10px]">
          pri {action.priority}
        </Badge>
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={onEdit}
          title="Edit action"
        >
          <Pencil size={13} />
        </Button>
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={onDelete}
          title="Delete action"
        >
          <Trash2 size={13} />
        </Button>
      </div>
    </div>
  )
}

function CountBadge({
  count,
  label,
  title,
}: {
  count: number
  label: string
  title: string
}) {
  if (count === 0) {
    return (
      <span
        title={title}
        className="rounded-[3px] border-[0.5px] border-dashed border-[var(--rule)] px-1.5 py-[1px] font-mono text-[10px] text-[var(--ink-3)]/60"
      >
        {label}
      </span>
    )
  }
  return (
    <span
      title={title}
      className="rounded-[3px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-1.5 py-[1px] font-mono text-[10px] text-[var(--ink)]"
    >
      {label} {count}
    </span>
  )
}
